/**
 * features/hr/DeductionImport.tsx
 * ------------------------------------------------------------------
 * Bulk importer for third-party provider deductions, with a downloadable
 * template per provider type:
 *
 *   Bank loans (Stanbic, BBS, …)  → bank_scheme deductions. Sets the
 *       outstanding balance + monthly + start, so the auto-decrement
 *       engine runs each loan down to zero and auto-archives it.
 *   Medical aid (Bomaid, …)       → medical_aid deductions (open-ended;
 *       recurs at the employee contribution each pay run).
 *
 * Matches rows to staff by employee number, previews before writing, and
 * skips rows whose deduction (by loan reference / membership number)
 * already exists so a re-run never double-adds. Built on lib/hrImport.
 * ------------------------------------------------------------------
 */
import { useMemo, useState } from 'react';
import { Upload, Download, CheckCircle2, Loader2, X, Building2, HeartPulse } from 'lucide-react';
import { AccentButton } from '@/components/ui/glass';
import { hr } from '@/features/hr/hrWrite';
import { recordHrAudit } from '@/lib/hrAudit';
import { useAuth } from '@/contexts/AuthContext';
import { staffFullName, type HrStaff } from '@/lib/hr';
import { round2, fmtPula, type HrDeduction, type DeductionType, type FieldValue } from '@/lib/hrPayroll';
import {
  parseCsv, makeColFinder, cell, numOr, hasValue, downloadCsv,
} from '@/lib/hrImport';

type Kind = 'bank' | 'medical';

interface ParsedDeduction {
  staff: HrStaff;
  type: DeductionType;
  label: string;
  values: Record<string, FieldValue>;
  summary: string;
  skip?: string; // reason it will be skipped (already exists)
}
interface Outcome {
  rows: ParsedDeduction[];
  unmatched: string[];
  errors: string[];
}

const TEMPLATES: Record<Kind, string> = {
  bank:
    'EmployeeNo,Bank,Reference,OutstandingBalance,MonthlyDeduction,StartDate\n' +
    'E001,Stanbic,LN-12345,18000,1500,2026-06-01\n' +
    'E002,BBS Bank,LN-77810,6400,800,2026-06-01\n' +
    'E003,FNB,LN-30021,24000,2000,2026-06-01',
  medical:
    'EmployeeNo,Scheme,MembershipNumber,TotalMonthlyPremium,EmployeeContribution,EmployerContribution,Plan,Dependants\n' +
    'E001,Bomaid,BM-00998,1800,900,900,Standard,2\n' +
    'E002,BPOMAS,BP-01242,1200,600,600,Standard,0\n' +
    'E003,Pula Medical,PM-55310,2400,1600,800,Premium,3',
};

const KIND_META: Record<Kind, { label: string; icon: typeof Building2; file: string }> = {
  bank: { label: 'Bank deductions (any bank)', icon: Building2, file: 'lkc_bank_deductions_template.csv' },
  medical: { label: 'Medical aid (any scheme)', icon: HeartPulse, file: 'lkc_medical_aid_template.csv' },
};

export default function DeductionImport({
  staff, deductions, onClose,
}: {
  staff: HrStaff[];
  deductions: HrDeduction[];
  onClose: () => void;
}) {
  const [kind, setKind] = useState<Kind>('bank');
  const { userData } = useAuth();
  const actor = { id: userData?.uid ?? 'unknown', email: userData?.email ?? null };
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<number | null>(null);

  const staffByEmp = useMemo(() => {
    const m = new Map<string, HrStaff>();
    for (const s of staff) if (s.employeeNo) m.set(s.employeeNo.trim().toLowerCase(), s);
    return m;
  }, [staff]);

  function existingActive(staffId: string, type: DeductionType, refKey: string, ref: string): boolean {
    if (!ref) return false;
    const r = ref.trim().toLowerCase();
    return deductions.some(
      (d) => d.staffId === staffId && d.type === type && d.status === 'active' &&
        String(d.values?.[refKey] ?? '').trim().toLowerCase() === r,
    );
  }

  function parse(src: string) {
    setDone(null);
    const p = parseCsv(src);
    if (p.error) { setOutcome({ rows: [], unmatched: [], errors: [p.error] }); return; }
    const find = makeColFinder(p.headers);
    const iEmp = find(['employeeno', 'empno', 'staffno', 'employee', 'staff', 'id']);
    if (iEmp < 0) {
      setOutcome({ rows: [], unmatched: [], errors: ['The file needs an employee-number column (e.g. "EmployeeNo").'] });
      return;
    }

    const rows: ParsedDeduction[] = [];
    const unmatched: string[] = [];
    const errors: string[] = [];

    if (kind === 'bank') {
      const iBank = find(['bank', 'bankname', 'scheme', 'lender']);
      const iRef = find(['reference', 'loanaccount', 'accountnumber', 'loanref', 'ref']);
      const iBal = find(['outstandingbalance', 'balance', 'outstanding', 'originalamount', 'amount']);
      const iMonthly = find(['monthlydeduction', 'monthly', 'instalment', 'installment', 'repayment']);
      const iStart = find(['startdate', 'firstdeduction', 'date']);
      const today = new Date().toISOString().slice(0, 10);
      p.rows.forEach((r, li) => {
        const empRaw = cell(r, iEmp);
        if (!empRaw) { errors.push(`Row ${li + 2}: missing employee number.`); return; }
        const s = staffByEmp.get(empRaw.trim().toLowerCase());
        if (!s) { unmatched.push(empRaw); return; }
        const monthly = numOr(cell(r, iMonthly));
        const balance = numOr(cell(r, iBal));
        if (monthly <= 0) { errors.push(`Row ${li + 2} (${empRaw}): monthly deduction is required.`); return; }
        const bank = cell(r, iBank).trim();
        if (!bank) { errors.push(`Row ${li + 2} (${empRaw}): bank name is required.`); return; }
        const ref = cell(r, iRef).trim();
        const start = hasValue(cell(r, iStart)) ? cell(r, iStart).trim() : today;
        const dup = existingActive(s.id, 'bank_scheme', 'reference', ref);
        rows.push({
          staff: s,
          type: 'bank_scheme',
          label: 'Bank Scheme',
          values: {
            bankName: bank,
            reference: ref || null,
            originalAmount: round2(balance),
            remainingBalance: round2(balance),
            monthlyDeduction: round2(monthly),
            startDate: start,
          },
          summary: `${bank} · ${fmtPula(round2(monthly))}/mo · outstanding ${fmtPula(round2(balance))}`,
          skip: dup ? 'already on file' : undefined,
        });
      });
    } else {
      const iScheme = find(['scheme', 'provider', 'medicalaid', 'fund']);
      const iMember = find(['membershipnumber', 'membernumber', 'membership', 'member']);
      const iPremium = find(['totalmonthlypremium', 'premium', 'totalpremium']);
      const iEmp2 = find(['employeecontribution', 'employee', 'staffcontribution', 'deduction']);
      const iEmployer = find(['employercontribution', 'employer', 'schoolcontribution']);
      const iPlan = find(['plan', 'option']);
      const iDep = find(['dependants', 'dependents', 'deps']);
      p.rows.forEach((r, li) => {
        const empRaw = cell(r, iEmp);
        if (!empRaw) { errors.push(`Row ${li + 2}: missing employee number.`); return; }
        const s = staffByEmp.get(empRaw.trim().toLowerCase());
        if (!s) { unmatched.push(empRaw); return; }
        const premium = numOr(cell(r, iPremium));
        const empContrib = hasValue(cell(r, iEmp2)) ? numOr(cell(r, iEmp2)) : round2(premium / 2);
        if (empContrib <= 0) { errors.push(`Row ${li + 2} (${empRaw}): employee contribution (or premium) is required.`); return; }
        const scheme = cell(r, iScheme).trim();
        if (!scheme) { errors.push(`Row ${li + 2} (${empRaw}): scheme name is required.`); return; }
        const member = cell(r, iMember).trim();
        const dup = existingActive(s.id, 'medical_aid', 'membershipNumber', member);
        rows.push({
          staff: s,
          type: 'medical_aid',
          label: scheme ? `Medical Aid — ${scheme}` : 'Medical Aid',
          values: {
            plan: cell(r, iPlan).trim() || null,
            membershipNumber: member || null,
            dependants: numOr(cell(r, iDep)),
            monthlyPremium: round2(premium || empContrib),
            employeeContribution: round2(empContrib),
            employerContribution: hasValue(cell(r, iEmployer)) ? round2(numOr(cell(r, iEmployer))) : null,
          },
          summary: `${scheme} · employee ${fmtPula(round2(empContrib))}/mo${premium ? ` (premium ${fmtPula(round2(premium))})` : ''}`,
          skip: dup ? 'already on file' : undefined,
        });
      });
    }
    setOutcome({ rows, unmatched, errors });
  }

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    parse(await file.text());
    e.target.value = '';
  }

  const toImport = outcome?.rows.filter((r) => !r.skip) ?? [];

  async function commit() {
    if (toImport.length === 0) return;
    setBusy(true);
    let saved = 0;
    try {
      for (const row of toImport) {
        await hr.deductions.add({
          staffId: row.staff.id,
          type: row.type,
          label: row.label,
          status: 'active',
          values: row.values,
          customFields: [],
        });
        saved++;
      }
      await recordHrAudit({
        action: 'deduction.import',
        actor,
        after: { kind, saved, source: 'provider-import' },
      });
      setDone(saved);
    } finally {
      setBusy(false);
    }
  }

  const matched = outcome?.rows.length ?? 0;
  const skipped = (outcome?.rows.length ?? 0) - toImport.length;

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between sticky top-0 bg-white dark:bg-gray-800 z-10">
          <h2 className="font-semibold text-gray-800 dark:text-gray-100">Import provider deductions</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
        </div>

        <div className="p-5 space-y-4">
          {/* Provider type toggle */}
          <div className="flex gap-2">
            {(Object.keys(KIND_META) as Kind[]).map((k) => {
              const M = KIND_META[k];
              const active = kind === k;
              return (
                <button
                  key={k}
                  onClick={() => { setKind(k); setOutcome(null); setDone(null); }}
                  className={`flex items-center gap-2 text-sm font-medium px-3 py-2 rounded-xl border ${
                    active
                      ? 'border-[#1b3a6b] bg-[#1b3a6b]/10 text-[#1b3a6b] dark:text-[#93c5fd]'
                      : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  <M.icon size={15} /> {M.label}
                </button>
              );
            })}
          </div>

          <p className="text-sm text-gray-500 dark:text-gray-400">
            {kind === 'bank'
              ? 'One row per loan, matched on employee number. The outstanding balance + monthly amount drive the auto-decrement — each loan runs down to zero and archives itself.'
              : 'One row per member, matched on employee number. The employee contribution is deducted each pay run (defaults to half the premium if left blank).'}
          </p>

          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={() => downloadCsv(KIND_META[kind].file, TEMPLATES[kind])}
              className="inline-flex items-center gap-2 text-sm font-medium px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              <Download size={15} /> Download template
            </button>
            <label className="inline-flex items-center gap-2 text-sm font-medium px-3 py-2 rounded-xl bg-[#1b3a6b] text-white hover:bg-[#2d5295] cursor-pointer">
              <Upload size={15} /> Choose CSV…
              <input type="file" accept=".csv,text/csv" onChange={onFile} className="hidden" />
            </label>
          </div>

          {outcome && (
            <div className="space-y-3">
              {outcome.errors.length > 0 && (
                <div className="rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-3 text-sm text-red-700 dark:text-red-300">
                  {outcome.errors.map((e, i) => <p key={i}>{e}</p>)}
                </div>
              )}

              <div className="flex flex-wrap gap-2 text-xs">
                <span className="px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300 font-semibold">{toImport.length} to import</span>
                {skipped > 0 && <span className="px-2 py-1 rounded-full bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300 font-semibold">{skipped} already on file</span>}
                {outcome.unmatched.length > 0 && (
                  <span className="px-2 py-1 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 font-semibold">{outcome.unmatched.length} unmatched</span>
                )}
              </div>

              {outcome.unmatched.length > 0 && (
                <p className="text-xs text-amber-700 dark:text-amber-300">
                  No staff found for: {outcome.unmatched.slice(0, 12).join(', ')}{outcome.unmatched.length > 12 ? '…' : ''}.
                </p>
              )}

              {matched > 0 && (
                <div className="overflow-x-auto rounded-xl border border-gray-100 dark:border-gray-700">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 dark:bg-gray-700/40 text-gray-500 dark:text-gray-400 text-xs">
                      <tr>
                        <th className="text-left px-3 py-2">Employee</th>
                        <th className="text-left px-3 py-2">Deduction</th>
                        <th className="text-right px-3 py-2">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {outcome.rows.map((r, i) => (
                        <tr key={`${r.staff.id}-${i}`} className="border-t border-gray-100 dark:border-gray-700">
                          <td className="px-3 py-2">
                            <div className="font-medium text-gray-800 dark:text-gray-100">{staffFullName(r.staff)}</div>
                            <div className="text-xs text-gray-400">#{r.staff.employeeNo}</div>
                          </td>
                          <td className="px-3 py-2 text-gray-600 dark:text-gray-300">{r.summary}</td>
                          <td className="px-3 py-2 text-right">
                            {r.skip
                              ? <span className="text-xs text-gray-400">skipped — {r.skip}</span>
                              : <span className="text-xs text-emerald-600 dark:text-emerald-400">will add</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {done != null && (
            <div className="rounded-xl border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20 p-3 text-sm text-emerald-700 dark:text-emerald-300 flex items-center gap-2">
              <CheckCircle2 size={16} /> Imported {done} deduction{done === 1 ? '' : 's'}.
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-100 dark:border-gray-700 sticky bottom-0 bg-white dark:bg-gray-800">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-xl dark:text-gray-300 dark:hover:bg-gray-700">
            {done != null ? 'Close' : 'Cancel'}
          </button>
          <AccentButton
            icon={busy ? Loader2 : CheckCircle2}
            disabled={busy || toImport.length === 0 || done != null}
            onClick={commit}
          >
            {busy ? 'Importing…' : `Import ${toImport.length || ''}`.trim()}
          </AccentButton>
        </div>
      </div>
    </div>
  );
}
